import { apiService } from './api';
import { Client, ClientCreate, PaginationParams } from '../types';

export interface ClientSettings {
  notifyOnInspectionComplete: boolean;
  notifyOnHighRiskFindings: boolean;
  weeklyDigest: boolean;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  defaultInspectionFrequencyDays: number;
  escalationRiskThreshold: string;
}

type ClientSettingsPayload = {
  notify_on_inspection_complete: boolean;
  notify_on_high_risk_findings: boolean;
  weekly_digest: boolean;
  primary_contact_name: string;
  primary_contact_email: string;
  primary_contact_phone: string;
  default_inspection_frequency_days: number;
  escalation_risk_threshold: string | number;
};

const fromSettingsPayload = (settings: ClientSettingsPayload): ClientSettings => ({
  notifyOnInspectionComplete: settings.notify_on_inspection_complete,
  notifyOnHighRiskFindings: settings.notify_on_high_risk_findings,
  weeklyDigest: settings.weekly_digest,
  primaryContactName: settings.primary_contact_name,
  primaryContactEmail: settings.primary_contact_email,
  primaryContactPhone: settings.primary_contact_phone,
  defaultInspectionFrequencyDays: settings.default_inspection_frequency_days,
  escalationRiskThreshold: typeof settings.escalation_risk_threshold === 'number'
    ? (settings.escalation_risk_threshold >= 20 ? 'very_high' : settings.escalation_risk_threshold >= 15 ? 'high' : settings.escalation_risk_threshold >= 5 ? 'moderate' : 'low')
    : settings.escalation_risk_threshold,
});

const toSettingsPayload = (settings: ClientSettings): ClientSettingsPayload => ({
  notify_on_inspection_complete: settings.notifyOnInspectionComplete,
  notify_on_high_risk_findings: settings.notifyOnHighRiskFindings,
  weekly_digest: settings.weeklyDigest,
  primary_contact_name: settings.primaryContactName,
  primary_contact_email: settings.primaryContactEmail,
  primary_contact_phone: settings.primaryContactPhone,
  default_inspection_frequency_days: settings.defaultInspectionFrequencyDays,
  escalation_risk_threshold: settings.escalationRiskThreshold,
});

export class ClientsService {
  async getClients(params?: PaginationParams): Promise<Client[]> {
    return apiService.get<Client[]>('/clients/', params);
  }

  async createClient(data: ClientCreate): Promise<Client> {
    return apiService.post<Client>('/clients/', data);
  }

  async getClientById(id: string): Promise<Client> {
    return apiService.get<Client>(`/clients/${id}`);
  }

  async deleteClient(id: string): Promise<void> {
    return apiService.delete<void>(`/clients/${id}`);
  }

  async getSettings(clientId: string): Promise<ClientSettings> {
    const settings = await apiService.get<ClientSettingsPayload>(
      `/clients/${clientId}/settings`
    );
    return fromSettingsPayload(settings);
  }

  async saveSettings(
    clientId: string,
    settings: ClientSettings
  ): Promise<ClientSettings> {
    const savedSettings = await apiService.put<ClientSettingsPayload>(
      `/clients/${clientId}/settings`,
      toSettingsPayload(settings)
    );
    return fromSettingsPayload(savedSettings);
  }
}

export const clientsService = new ClientsService();
